/**
 * ChatBubble — shared message row renderer for 1:1 twin chat and room chat.
 *
 * Renders the full row: [avatar] [sender-info] [bubble-content] for assistant,
 * and [bubble-content] [user-initials] for user.
 *
 * Surfaces pass `senderInfo` only when the chat is multi-speaker (room).
 * The avatar is per-message in rooms; in 1:1 pass `avatar={null}` for
 * subsequent messages in a group to reserve alignment space without showing it.
 */

import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { ScrollableImagePreview } from '@/components/media/ScrollableImagePreview'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { cn } from '@/lib/utils'
import { getAvatarPaletteForSeed } from '@/lib/utils/avatar'
import { MENTION_TAG_TEXT_CLASS } from '@/components/chat/MentionHighlightTextarea'
import { InsightsAssistantAvatar } from '@/components/chat/InsightsAssistantAvatar/InsightsAssistantAvatar'

// ---------------------------------------------------------------------------
// @mention highlighting for user messages
// ---------------------------------------------------------------------------

const MENTION_RE = /@(\w+)/g

/**
 * Takes a plain string and returns React nodes where `@Word` tokens are
 * wrapped in a <span> with the tag color the composer shows while typing.
 */
function renderWithMentions(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = MENTION_RE.exec(text)) !== null) {
    // Push text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    // Push the styled mention
    parts.push(
      <span key={match.index} className={MENTION_TAG_TEXT_CLASS}>
        {match[0]}
      </span>,
    )
    lastIndex = MENTION_RE.lastIndex
  }

  // If no mentions found, return the original string (avoids unnecessary spans)
  if (parts.length === 0) return text

  // Push trailing text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return <Fragment>{parts}</Fragment>
}

export type BubblePosition = 'solo' | 'first' | 'middle' | 'last'

export interface ChatBubbleAvatarInfo {
  url: string | null
  fallbackInitials: string
  /** Used to derive a deterministic colour palette for the avatar fallback. */
  seed?: string
  /** Explicit palette — overrides seed-based palette (e.g. for George's fixed indigo). */
  palette?: { bg: string; fg: string }
}

export interface ChatBubbleSenderInfo {
  name: string
  tagline?: string | null
  /** Optional badge node rendered inline after the name (e.g. "Research Assistant"). */
  badge?: React.ReactNode
}

export interface ChatBubbleProps {
  role: 'user' | 'assistant'
  content: string
  /** Bubble shape for grouped conversations. Defaults to 'solo'. */
  position?: BubblePosition
  /**
   * Assistant-side avatar.
   * - `ChatBubbleAvatarInfo` → render the avatar.
   * - `null` → reserve space without rendering (use for 2nd+ messages in a 1:1 group).
   * - `undefined` → no avatar and no reserved space.
   */
  avatar?: ChatBubbleAvatarInfo | null
  /** Rendered as a small name/tagline blurb above the assistant bubble (room only). */
  senderInfo?: ChatBubbleSenderInfo
  /** Inline images attached to a user message. */
  images?: Array<{ base64: string; mimeType: string }>
  /** Initials shown in the user's avatar circle (room only). Omit for no user avatar. */
  userInitials?: string
  /** Extra content rendered below the markdown body (e.g. SampleArtifactInline). */
  extraContent?: React.ReactNode
  /** Optional surface-specific renderer for assistant content. */
  renderedContent?: React.ReactNode
  /** The Insights assistant (`senderType 'george'`): its icon replaces the initials avatar. */
  isGeorge?: boolean
  /** Removes the bubble background — used for thinking/typing indicators. */
  noBubbleBg?: boolean
  /**
   * Assistant only: a small node rendered in the avatar column, centered just
   * under the avatar (e.g. the answer grounding meter). With `avatar={null}` it
   * sits under the reserved avatar space, so it keeps the same offset.
   */
  belowAvatar?: React.ReactNode
  /** Assistant only: a reply quote rendered inside the bubble, above the text (room reactors). */
  quote?: React.ReactNode
}

const FALLBACK_IMAGE = '/images/twin-fallback.png'

const assistantRadii: Record<BubblePosition, string> = {
  solo: 'rounded-tl-sm rounded-tr-xl rounded-bl-xl rounded-br-xl',
  first: 'rounded-tl-sm rounded-tr-xl rounded-bl-sm rounded-br-sm',
  middle: 'rounded-tl-sm rounded-tr-sm rounded-bl-sm rounded-br-sm',
  last: 'rounded-tl-sm rounded-tr-sm rounded-bl-xl rounded-br-xl',
}

const userRadii: Record<BubblePosition, string> = {
  solo: 'rounded-tl-xl rounded-tr-sm rounded-bl-xl rounded-br-xl',
  first: 'rounded-tl-xl rounded-tr-sm rounded-bl-sm rounded-br-sm',
  middle: 'rounded-tl-sm rounded-tr-sm rounded-bl-sm rounded-br-sm',
  last: 'rounded-tl-sm rounded-tr-sm rounded-bl-xl rounded-br-xl',
}

const IMAGE_ONLY_PLACEHOLDER = '(Image)'

export function ChatBubble(props: ChatBubbleProps) {
  const {
    role,
    content,
    position = 'solo',
    avatar,
    senderInfo,
    images,
    userInitials,
    extraContent,
    renderedContent,
    isGeorge = false,
    noBubbleBg = false,
    belowAvatar,
    quote,
  } = props

  const { t } = useTranslation('app')
  const isUser = role === 'user'
  const { displayUrl: avatarThumbUrl, handleImageError: handleAvatarImageError } = usePersonaThumbFallback(
    avatar?.url,
    64,
  )

  if (isUser) {
    const hasText = Boolean(content && content !== IMAGE_ONLY_PLACEHOLDER)
    const hasInlineImages = Boolean(images && images.length > 0)
    // Nothing to put in the bubble — e.g. a room image-only message whose image
    // is rendered standalone by the caller. Don't render an empty pill.
    if (!hasText && !hasInlineImages) return null
    return (
      <div className="flex items-start gap-2 flex-row-reverse animate-[chat-message-in_0.28s_ease-out_both] motion-reduce:animate-none">
        {/* User bubble */}
        <div
          className={cn(
            'max-w-[85%] px-4 py-3',
            userRadii[position],
            'bg-primary/30 text-foreground',
          )}
        >
          {images && images.length > 0 && (
            <div className={cn('mb-2 flex gap-2', images.length > 1 && 'flex-wrap')}>
              {images.map((img, i) => (
                <ScrollableImagePreview
                  key={i}
                  src={`data:${img.mimeType};base64,${img.base64}`}
                  alt={t('chatChrome.attachedImageAlt', { index: i + 1 })}
                  thumbnailClassName="h-36 w-36"
                />
              ))}
            </div>
          )}
          {content && content !== IMAGE_ONLY_PLACEHOLDER && (
            <p className="text-base leading-relaxed whitespace-pre-wrap">{renderWithMentions(content)}</p>
          )}
        </div>

        {/* User initials circle (room only) */}
        {userInitials && (
          <div className="mt-1 h-7 w-7 shrink-0 flex items-center justify-center rounded-full bg-twin-chat-bubble/40 font-display text-2xs font-normal text-twin-chat-fg-muted">
            {userInitials}
          </div>
        )}
      </div>
    )
  }

  // Assistant row: [avatar] [senderInfo + bubble]
  const avatarPalette = avatar?.palette
    ?? (avatar?.seed ? getAvatarPaletteForSeed(avatar.seed) : { bg: 'hsl(var(--twin-chat-bubble))', fg: 'hsl(var(--twin-chat-fg))' })

  // Light mode softens the bubble to 60% over the page, so long threads read airy;
  // dark mode keeps the solid token, since an alpha there would sink into the page.
  const bubbleBg = noBubbleBg ? '' : 'bg-study-chat-ai-bubble/60 dark:bg-study-chat-ai-bubble text-foreground'
  const hasBelowAvatar = belowAvatar != null && belowAvatar !== false
  const hasAvatarColumn = avatar !== undefined || hasBelowAvatar

  return (
    <div className="flex items-start gap-2 animate-[chat-message-in_0.28s_ease-out_both] motion-reduce:animate-none">
      {/* Avatar column — reserve space if avatar is not undefined; belowAvatar sits under it */}
      {hasAvatarColumn && (
        <div
          className="mt-1 flex w-7 shrink-0 flex-col items-center"
          data-testid="chat-bubble-avatar-column"
        >
          <div className="h-7 w-7">
            {avatar != null && isGeorge && (
              <InsightsAssistantAvatar className="h-7 w-7" testId="chat-bubble-assistant-icon" />
            )}
            {avatar != null && !isGeorge && (
              <Avatar className="h-7 w-7 rounded-full">
                {avatarThumbUrl ? (
                  <AvatarImage
                    src={avatarThumbUrl}
                    alt={senderInfo?.name ?? ''}
                    className="object-cover object-top"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      handleAvatarImageError(() => {
                        target.src = FALLBACK_IMAGE
                      })
                    }}
                  />
                ) : null}
                <AvatarFallback
                  className="rounded-full text-[10px] font-medium"
                  style={{ backgroundColor: avatarPalette.bg, color: avatarPalette.fg }}
                >
                  {avatar.fallbackInitials}
                </AvatarFallback>
              </Avatar>
            )}
          </div>

          {hasBelowAvatar && (
            <div className="mt-1.5 flex justify-center" data-testid="chat-bubble-below-avatar">
              {belowAvatar}
            </div>
          )}
        </div>
      )}

      <div className={cn('flex min-w-0 flex-col', hasAvatarColumn ? 'flex-1' : 'max-w-[85%]')}>
        {/* Sender name / tagline blurb (room only) */}
        {senderInfo && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-muted-foreground">
              {senderInfo.name}
              {senderInfo.tagline && (
                <span className="font-normal"> · {senderInfo.tagline}</span>
              )}
            </span>
            {senderInfo.badge}
          </div>
        )}

        {/* Bubble */}
        <div
          className={cn(
            'min-w-0 px-4 py-3 text-twin-chat-fg',
            assistantRadii[position],
            bubbleBg,
          )}
        >
          {quote}
          {renderedContent ?? (content && content !== IMAGE_ONLY_PLACEHOLDER && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              // Message text (twin or user) never loads remote images: a markdown image
              // would make every viewer's browser fetch an arbitrary external URL.
              disallowedElements={['img']}
              components={{
                p: ({ children }) => (
                  <p className="mb-2 text-base leading-relaxed last:mb-0">{children}</p>
                ),
                ul: ({ children }) => <ul className="mb-2 ml-4 list-disc">{children}</ul>,
                ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal">{children}</ol>,
                code: ({ children }) => (
                  <code className="rounded bg-twin-chat-fg/10 px-1 text-sm">
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className="mb-2 overflow-x-auto rounded-md bg-twin-chat-fg/10 p-3">
                    {children}
                  </pre>
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          ))}

          {extraContent}
        </div>
      </div>
    </div>
  )
}

export { IMAGE_ONLY_PLACEHOLDER as CHAT_IMAGE_ONLY_PLACEHOLDER }

/** Utility kept here so callers don't re-implement grouping math. */
export function getBubblePosition(index: number, total: number): BubblePosition {
  if (total === 1) return 'solo'
  if (index === 0) return 'first'
  if (index === total - 1) return 'last'
  return 'middle'
}
